import {
  CreateScreenResponse,
  FetchScreenResponse,
  FilterSchemas,
  groupEnum,
  KNOWLEDGE_GRAPH_FILTERS,
  MARKET_FILTERS,
  ScreenerFilterGroupKeys,
  ScreenerFilters,
  screener,
  TableState,
  UpdateScreenResponse,
} from '@api';
import { deepEqual } from '@helpers';
import { SortingState } from '@tanstack/react-table';
import { WORKING_LIST_ID } from '@terminal/config/constants';
import {
  FilterGroups,
  SharedFilterState,
} from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ApiFetchResponse, apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { create } from '@terminal/utils/store/createStore';
import { NON_US_INDICES } from '@terminal/views/explore/components/explore-filters/asset-filter/utils';
import { checkIfFirstOptionIsNotChild } from '@terminal/widgets/scenario/utils/scenario-conditions/scenario-conditions';

import {
  ColumnMetaWithKey,
  DEFAULT_TAB,
  KNOWLEDGE_GRAPH_TAB,
  SavedCategorySetting,
} from '../results/screener-table-utils';
import {
  ASSET_FILTERS_KEY,
  assetFilter,
  clearActiveFilterOptions,
  getFilterOptionsPerGroup,
  getSavedScreenFilterGroups,
  resetKGTableState,
} from './filter-utils';
import {
  compareColumnsForKG,
  removeIdFromKgFilterName,
} from './kg-filter-utils';

export type ScreenerStore = {
  tableState: TableState;
  activeSavedScreen?: CreateScreenResponse;
  savedScreensWithInvalidWatchlistMap: { [key: string]: boolean };
  updateSavedScreensWithInvalidWatchlistMap: (id: string, val: boolean) => void;
  draftScreenFilterGroups?: FilterGroups<ScreenerFilterGroupKeys>;
  savedScreens: CreateScreenResponse[];
  groupNameMap: Record<ScreenerFilterGroupKeys, string>;
  handlePinnedColumns: (props: {
    columnsMeta: ColumnMetaWithKey[];
    pin: boolean;
  }) => void;
  handleColumnOrdering: (props: { tab: string; fields: string[] }) => void;
  handleColumnSorting: (props: SortingState) => void;
  initStore: () => Promise<void>;
  setActiveSavedScreen: (screenerId: string | null, setDraft?: boolean) => void;
  hasFilterOptionsChanged: (group: ScreenerFilterGroupKeys) => boolean;
  hasTableChanges: (tab: string, defaultTabOrdering?: string[]) => boolean;
  unsavedChanges: () => boolean;
  unsavedDraftChanges: () => boolean;
  saveNewFilter: (
    name: string
  ) => Promise<ApiFetchResponse<CreateScreenResponse>>;
  updateSavedFilter: (
    id: string,
    name?: string
  ) => Promise<ApiFetchResponse<UpdateScreenResponse>>;
  updateSavedScreen: (
    id: string,
    res: ApiFetchResponse<UpdateScreenResponse>
  ) => void;
  deleteSavedFilter: (screenId: string) => Promise<ApiFetchResponse<void>>;
  resetActiveFilterOptions: (
    group?: ScreenerFilterGroupKeys,
    resetToDefault?: boolean
  ) => void;
  createDraftScreen: (props?: { forceNew?: boolean; init?: boolean }) => void;
  resetTableState: (tab: string) => void;
  canCreateNewScreen: () => boolean;
  shouldPromptUnsavedChanges: () => boolean;
  isWorkingListActiveInDraft: () => boolean;
  isWorkingListActive: () => boolean;
  customDefaultTableTab?: string;
  setCustomDefaultTableTab: (tab?: string) => void;
} & SharedFilterState;

const getDefaultFilters = (filters: ScreenerFilters) =>
  filters.filter(f => f.visible);

const defaultTableState = {
  sorting: [],
  pinnedColumns: {},
  columnOrdering: {},
};

const defaultFilterGroups: FilterGroups<ScreenerFilterGroupKeys> = {
  [MARKET_FILTERS]: {
    activeFilterOptions: [],
    activeFilters: [],
    allFilters: [],
  },
  [KNOWLEDGE_GRAPH_FILTERS]: {
    activeFilterOptions: [],
    activeFilters: [],
    allFilters: [],
  },
};

export const defaultGroupNameMap: Record<ScreenerFilterGroupKeys, string> = {
  [MARKET_FILTERS]: '',
  [KNOWLEDGE_GRAPH_FILTERS]: '',
};

export const useScreenerStore = create<ScreenerStore>((set, get) => ({
  setCustomDefaultTableTab: tab => {
    set({ customDefaultTableTab: tab });
  },
  filterGroups: defaultFilterGroups,
  groupNameMap: defaultGroupNameMap,
  tableState: defaultTableState,
  savedScreens: [],
  activeSavedScreen: undefined,
  initStore: async () => {
    const [resFilters, resSavedScreens] = await Promise.all([
      apiFetch<FilterSchemas>(screener.getFilters.path(), { method: 'get' }),
      apiFetch<FetchScreenResponse>(screener.getSavedScreens.path, {
        method: 'get',
      }),
    ]);
    const isBasicUser = useSubscription.getState().isBasicUser();

    if (!resFilters.error) {
      const { groups } = resFilters.data;
      const groupNameMap: Record<ScreenerFilterGroupKeys, string> = {
        ...defaultGroupNameMap,
      };

      const filterGroups: FilterGroups<ScreenerFilterGroupKeys> = {
        [MARKET_FILTERS]: { ...defaultFilterGroups[MARKET_FILTERS] },
        [KNOWLEDGE_GRAPH_FILTERS]: {
          ...defaultFilterGroups[KNOWLEDGE_GRAPH_FILTERS],
        },
      };

      groups.forEach(group => {
        const isMarketFilterGroup = group.group === MARKET_FILTERS;
        let groupSchemas = group.schemas;

        groupNameMap[group.group] = group.group_name;

        if (isMarketFilterGroup) {
          groupSchemas = [
            assetFilter,
            ...group.schemas.map(schema => {
              if (schema.key === 'market_index') {
                const marketIndexOptions = schema.config.options.filter(
                  option =>
                    isBasicUser ? !NON_US_INDICES.includes(option.name) : true
                );
                return {
                  ...schema,
                  config: {
                    ...schema.config,
                    options: marketIndexOptions,
                  },
                };
              }

              return schema;
            }),
          ];
        }

        filterGroups[group.group] = {
          activeFilterOptions: [],
          activeFilters: getDefaultFilters(groupSchemas),
          allFilters: groupSchemas,
        };
      });
      set({ filterGroups, groupNameMap });
    }

    if (!resSavedScreens.error) {
      set({ savedScreens: resSavedScreens.data.screens });
    }
  },
  saveNewFilter: async name => {
    const { path, schema } = screener.saveNewFilter;
    const { filterGroups, tableState } = get();
    const filterOptionGroups = getFilterOptionsPerGroup(filterGroups);
    const res = await apiFetch<CreateScreenResponse>(path, {
      schema,
      method: 'post',
      body: {
        favorite: false,
        name,
        config: {
          tableState,
          filterOptionGroups,
          isDraft: false,
          version: 'v1',
        },
      },
    });

    if (!res.error) {
      set(state => {
        return {
          savedScreens: [...state.savedScreens, res.data],
          activeSavedScreen: res.data,
          tableState: res.data.config.tableState,
          draftScreenFilterGroups: undefined,
        };
      });
    }

    return res;
  },
  updateSavedFilter: async (id, name) => {
    const { path, schema } = screener.updateSavedFilter;
    const { filterGroups, tableState, activeSavedScreen } = get();
    const filterOptionGroups = getFilterOptionsPerGroup(filterGroups);

    const res = await apiFetch<UpdateScreenResponse>(path(id), {
      schema,
      method: 'put',
      body: {
        favorite: false,
        name: name ?? activeSavedScreen?.name,
        config: {
          tableState,
          filterOptionGroups,
          isDraft: false,
          version: 'v1',
        },
      },
    });

    return res;
  },
  updateSavedScreen: (
    id: string,
    res: ApiFetchResponse<UpdateScreenResponse>
  ) => {
    if (!res.error) {
      set(state => ({
        savedScreens: state.savedScreens.map(filter =>
          filter.id === id ? res.data : filter
        ),
        activeSavedScreen:
          state.activeSavedScreen?.id === id
            ? res.data
            : state.activeSavedScreen,
        savedScreensWithInvalidWatchlistMap: {
          ...state.savedScreensWithInvalidWatchlistMap,
          [id]: false,
        },
      }));
    }
  },
  deleteSavedFilter: async screenId => {
    const { path } = screener.deleteSavedFilter;
    const res = await apiFetch<void>(path(screenId), {
      method: 'delete',
    });

    if (!res.error) {
      set(state => ({
        savedScreens: state.savedScreens.filter(
          filter => filter.id !== screenId
        ),
      }));
    }

    return res;
  },
  setActiveSavedScreen: screenerId => {
    const {
      savedScreens,
      filterGroups,
      activeSavedScreen,
      draftScreenFilterGroups,
    } = get();

    //setting draft as current screen
    if (screenerId === null) {
      set({
        activeSavedScreen: undefined,
        filterGroups: draftScreenFilterGroups,
      });
      return;
    }

    if (activeSavedScreen?.id !== screenerId) {
      const screen = savedScreens.find(f => f.id === screenerId);

      if (screen) {
        const { tableState = defaultTableState } = screen.config;
        const { filterGroups: savedFilterGroups, hasInvalidWatchlist } =
          getSavedScreenFilterGroups(filterGroups, screen.config);

        set(({ savedScreensWithInvalidWatchlistMap }) => ({
          filterGroups: savedFilterGroups,
          tableState,
          activeSavedScreen: screen,
          savedScreensWithInvalidWatchlistMap: {
            ...savedScreensWithInvalidWatchlistMap,
            [screenerId]: hasInvalidWatchlist,
          },
        }));
      }
    }
  },
  hasTableChanges: (tab: string, defaultTabOrdering?: string[]) => {
    const { tableState, activeSavedScreen } = get();

    const sanitizedDefaultTabOrdering = (defaultTabOrdering ?? []).map(
      removeIdFromKgFilterName
    );
    const sanitizedTableStateOrdering = (
      tableState.columnOrdering[tab] ?? []
    ).map(removeIdFromKgFilterName);

    if (!activeSavedScreen) {
      if (
        tableState.pinnedColumns[tab]?.length ||
        tableState.sorting.some(sort => defaultTabOrdering?.includes(sort.id))
      ) {
        return true;
      }

      if (defaultTabOrdering && tableState.columnOrdering[tab]) {
        const columnsMatch = deepEqual(
          sanitizedDefaultTabOrdering,
          sanitizedTableStateOrdering
        );
        return !columnsMatch;
      }

      return false;
    }

    const savedTableState = activeSavedScreen?.config.tableState;

    const sortMatches = deepEqual(
      savedTableState?.sorting.filter(sort =>
        defaultTabOrdering?.includes(sort.id)
      ),
      tableState.sorting.filter(sort => defaultTabOrdering?.includes(sort.id))
    );
    const pinsMatch =
      tab === DEFAULT_TAB
        ? deepEqual(savedTableState.pinnedColumns, tableState.pinnedColumns)
        : deepEqual(
            savedTableState?.pinnedColumns[tab],
            tableState.pinnedColumns[tab]
          );

    const sanitizedSavedTableStateOrdering =
      savedTableState?.columnOrdering[tab]?.map(removeIdFromKgFilterName) ?? [];

    const colsMatchDefault = defaultTabOrdering
      ? deepEqual(sanitizedDefaultTabOrdering, sanitizedTableStateOrdering)
      : true;

    const isKGTab = tab === KNOWLEDGE_GRAPH_TAB;
    const hasSavedTableStateForTab = savedTableState?.columnOrdering[tab];

    let columnsMatch = false;

    if (hasSavedTableStateForTab) {
      columnsMatch = isKGTab
        ? compareColumnsForKG(
            sanitizedSavedTableStateOrdering,
            sanitizedTableStateOrdering
          )
        : deepEqual(
            sanitizedSavedTableStateOrdering,
            sanitizedTableStateOrdering
          );
    } else {
      columnsMatch = colsMatchDefault;
    }

    return !sortMatches || !pinsMatch || !columnsMatch;
  },
  unsavedChanges: () => {
    const { tableState, activeSavedScreen, hasFilterOptionsChanged } = get();

    const anyFilterOptionsChanged = groupEnum.options.some(group =>
      hasFilterOptionsChanged(group)
    );

    if (!activeSavedScreen) {
      return anyFilterOptionsChanged;
    }

    const tableStateMatchSaved = () => {
      const columnsMatch = Object.entries(tableState.columnOrdering).every(
        ([name, value]) => {
          const savedValue =
            activeSavedScreen.config.tableState?.columnOrdering?.[name];

          // If saved screen doesn't have ordering for this tab, treat as matching
          if (!savedValue) {
            return true;
          }

          // This handles cases where:
          // 1. New columns were added (like insights) after the screen was saved
          // 2. Old columns were removed from the schema
          const currentArray = value;
          const savedArray = savedValue;
          const commonColumns = savedArray.filter(col =>
            currentArray.includes(col)
          );

          const commonColumnsInCurrentOrder = currentArray.filter(col =>
            savedArray.includes(col)
          );
          return deepEqual(commonColumns, commonColumnsInCurrentOrder);
        }
      );

      const fieldsMatch = (['sorting', 'pinnedColumns'] as const).every(field =>
        deepEqual(
          activeSavedScreen.config.tableState?.[field],
          tableState[field]
        )
      );

      return columnsMatch && fieldsMatch;
    };

    return anyFilterOptionsChanged || !tableStateMatchSaved();
  },
  unsavedDraftChanges: () => {
    const { draftScreenFilterGroups } = get();
    return (
      !!draftScreenFilterGroups &&
      Object.values(draftScreenFilterGroups).some(
        filterState => filterState.activeFilterOptions.length
      )
    );
  },
  hasFilterOptionsChanged: group => {
    const { activeSavedScreen, filterGroups } = get();
    const activeFilterOptions = filterGroups[group].activeFilterOptions;
    const config = activeSavedScreen?.config;

    const hasActiveOptions = activeFilterOptions.every(({ options }) =>
      checkIfFirstOptionIsNotChild(options)
    );

    if (!config) {
      return !!activeFilterOptions.length && hasActiveOptions;
    }

    return (
      hasActiveOptions &&
      !deepEqual(config.filterOptionGroups?.[group], activeFilterOptions)
    );
  },
  resetActiveFilterOptions: (
    group?: ScreenerFilterGroupKeys,
    resetToDefault?: boolean
  ) => {
    const { filterGroups, activeSavedScreen, tableState } = get();

    if (activeSavedScreen && !resetToDefault) {
      const { filterGroups: savedFilterGroups, hasInvalidWatchlist } =
        getSavedScreenFilterGroups(filterGroups, activeSavedScreen.config);
      const newFilterGroups = group
        ? { ...filterGroups, [group]: savedFilterGroups[group] }
        : savedFilterGroups;

      let newTableState = tableState;
      const resetAll = !group;
      const hadKGFilters =
        !!filterGroups[KNOWLEDGE_GRAPH_FILTERS].activeFilterOptions.length;

      if (hadKGFilters && (group === KNOWLEDGE_GRAPH_FILTERS || resetAll)) {
        const savedScreenTableState = activeSavedScreen.config.tableState;
        newTableState = resetKGTableState(tableState, savedScreenTableState);
      }

      set(({ savedScreensWithInvalidWatchlistMap }) => ({
        filterGroups: newFilterGroups,
        tableState: newTableState,
        savedScreensWithInvalidWatchlistMap: {
          ...savedScreensWithInvalidWatchlistMap,
          [activeSavedScreen.id]: hasInvalidWatchlist,
        },
      }));
    } else {
      const keys = group ? [group] : [...groupEnum.options];
      const newFilterGroups = { ...filterGroups };
      keys.forEach(key => {
        newFilterGroups[key].activeFilterOptions = [];
      });

      set({ filterGroups: newFilterGroups });
    }
  },
  handlePinnedColumns: ({ columnsMeta, pin }) => {
    const updatedPinned = (
      prev: SavedCategorySetting
    ): SavedCategorySetting => {
      const category = columnsMeta[0].key;
      const fields = prev[category] ?? [];
      return {
        ...prev,
        [category]: pin
          ? [...fields, ...columnsMeta.map(c => c.field)]
          : fields.filter(f => columnsMeta.every(c => c.field !== f)),
      };
    };

    set(({ tableState }) => ({
      tableState: {
        ...tableState,
        pinnedColumns: updatedPinned(tableState.pinnedColumns),
      },
    }));
  },
  handleColumnOrdering: ({ tab, fields }) => {
    const updateOrdering = (
      prev: SavedCategorySetting
    ): SavedCategorySetting => {
      return {
        ...prev,
        [tab]: fields,
      };
    };

    set(({ tableState }) => ({
      tableState: {
        ...tableState,
        columnOrdering: updateOrdering(tableState.columnOrdering),
      },
    }));
  },
  handleColumnSorting: (sorting: SortingState) => {
    set(({ tableState }) => ({
      tableState: {
        ...tableState,
        sorting,
      },
    }));
  },
  createDraftScreen: ({ init = false, forceNew = true } = {}) => {
    const { filterGroups, draftScreenFilterGroups } = get();
    let newFilterGroups = draftScreenFilterGroups;

    if (init) {
      newFilterGroups = filterGroups;
    } else if (forceNew || !draftScreenFilterGroups) {
      newFilterGroups = clearActiveFilterOptions(filterGroups);
    }

    set({
      tableState: defaultTableState,
      activeSavedScreen: undefined,
      filterGroups: newFilterGroups,
      draftScreenFilterGroups: newFilterGroups,
    });
  },
  resetTableState: (tab: string) => {
    const { activeSavedScreen, handleColumnSorting, tableState } = get();

    const { sorting, columnOrdering: currentOrdering } = tableState;

    const pinnedColumns = {
      ...tableState.pinnedColumns,
    };

    const columnOrdering = {
      ...tableState.columnOrdering,
    };

    if (!activeSavedScreen) {
      delete pinnedColumns[tab];
      delete columnOrdering[tab];

      handleColumnSorting(
        sorting.filter(sort => !currentOrdering[tab].includes(sort.id))
      );

      set({
        tableState: {
          sorting: [],
          pinnedColumns:
            tab == DEFAULT_TAB
              ? defaultTableState.pinnedColumns
              : pinnedColumns,
          columnOrdering: columnOrdering,
        },
      });
    } else {
      const savedTableState = activeSavedScreen.config.tableState;

      const newSort = [
        ...sorting.filter(sort => !currentOrdering[tab].includes(sort.id)), // keep sorts from other tabs
        ...savedTableState.sorting.filter(
          sort => currentOrdering[tab].includes(sort.id) // add back any sorts for the tab from saved
        ),
      ];

      handleColumnSorting(newSort);

      if (savedTableState.columnOrdering[tab]) {
        columnOrdering[tab] = savedTableState.columnOrdering[tab];
      } else {
        delete columnOrdering[tab];
      }

      if (savedTableState.pinnedColumns[tab]) {
        pinnedColumns[tab] = savedTableState.pinnedColumns[tab];
      } else {
        delete pinnedColumns[tab];
      }

      set(({ tableState }) => ({
        tableState: {
          ...tableState,
          columnOrdering: columnOrdering,
          pinnedColumns:
            tab == DEFAULT_TAB ? savedTableState.pinnedColumns : pinnedColumns,
        },
      }));
    }
  },
  canCreateNewScreen: () => {
    const { draftScreenFilterGroups, unsavedDraftChanges } = get();
    return !draftScreenFilterGroups || unsavedDraftChanges();
  },
  shouldPromptUnsavedChanges: () => {
    const { unsavedChanges, activeSavedScreen, isWorkingListActive } = get();
    return !isWorkingListActive() && unsavedChanges() && !!activeSavedScreen;
  },
  isWorkingListActiveInDraft: () => {
    const { draftScreenFilterGroups } = get();
    const assetsFilter = draftScreenFilterGroups?.[
      MARKET_FILTERS
    ].activeFilterOptions.find(f => f.id === ASSET_FILTERS_KEY);

    return !!assetsFilter?.options.some(o => o.key === WORKING_LIST_ID);
  },
  isWorkingListActive: () => {
    const { filterGroups } = get();
    const assetsFilter = filterGroups[MARKET_FILTERS].activeFilterOptions.find(
      f => f.id === ASSET_FILTERS_KEY
    );

    return !!assetsFilter?.options.some(o => o.key === WORKING_LIST_ID);
  },
  savedScreensWithInvalidWatchlistMap: {},
  updateSavedScreensWithInvalidWatchlistMap: (id, val) => {
    set(({ savedScreensWithInvalidWatchlistMap }) => ({
      savedScreensWithInvalidWatchlistMap: {
        ...savedScreensWithInvalidWatchlistMap,
        [id]: val,
      },
    }));
  },
}));
